import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  Palette,
  Building2,
  Globe,
  Image as ImageIcon,
  Type,
  Code,
  Save,
  Plus,
  RefreshCw,
  Eye,
  Mail,
} from 'lucide-react';

export default function WhiteLabelPage() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
  });
  const [branding, setBranding] = useState({
    organization_name: 'Lumina OS',
    logo_url: '',
    favicon_url: '',
    primary_color: '#3b82f6',
    secondary_color: '#10b981',
    accent_color: '#f59e0b',
    background_color: '#09090b',
    text_color: '#ffffff',
    font_family: 'Space Grotesk',
    custom_css: '',
    email_header_html: '',
    email_footer_html: '',
  });

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await api.get('/organizations');
      setOrganizations(response.data);
      if (response.data.length > 0 && !selectedOrg) {
        setSelectedOrg(response.data[0]);
        setBranding(response.data[0].branding || branding);
      }
    } catch (error) {
      // No organizations yet
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/organizations', formData);
      toast.success('Organization created successfully');
      setDialogOpen(false);
      loadOrganizations();
    } catch (error) {
      toast.error('Failed to create organization');
    }
  };

  const handleSaveBranding = async () => {
    if (!selectedOrg) {
      toast.error('Please select an organization first');
      return;
    }
    try {
      await api.put(`/organizations/${selectedOrg.id}/branding`, branding);
      toast.success('Branding saved successfully');
      loadOrganizations();
    } catch (error) {
      toast.error('Failed to save branding');
    }
  };

  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
    setBranding(org.branding || branding);
  };

  const colorInputs = [
    { key: 'primary_color', label: 'Primary Color', description: 'Buttons, links, active states' },
    { key: 'secondary_color', label: 'Secondary Color', description: 'Success states, positive actions' },
    { key: 'accent_color', label: 'Accent Color', description: 'Highlights, warnings' },
    { key: 'background_color', label: 'Background Color', description: 'Main app background' },
    { key: 'text_color', label: 'Text Color', description: 'Primary text color' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whitelabel-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            White-Label Settings
          </h1>
          <p className="text-zinc-400 mt-1">
            Customize branding for your organization
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setPreviewMode(!previewMode)}
            className="border-zinc-700"
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewMode ? 'Exit Preview' : 'Preview'}
          </Button>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-500"
            data-testid="add-org-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Organization
          </Button>
        </div>
      </div>

      {/* Organization Selector */}
      {organizations.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {organizations.map((org) => (
            <Button
              key={org.id}
              variant={selectedOrg?.id === org.id ? 'default' : 'outline'}
              onClick={() => handleOrgSelect(org)}
              className={selectedOrg?.id === org.id ? 'bg-blue-600' : 'border-zinc-700'}
            >
              <Building2 className="w-4 h-4 mr-2" />
              {org.name}
            </Button>
          ))}
        </div>
      )}

      {/* Preview Banner */}
      {previewMode && (
        <div
          className="p-4 rounded-lg border-2 border-dashed"
          style={{
            backgroundColor: branding.background_color,
            borderColor: branding.primary_color,
          }}
        >
          <div className="flex items-center gap-4">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-10" />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: branding.primary_color }}
              >
                {branding.organization_name.charAt(0)}
              </div>
            )}
            <span
              className="text-xl font-bold"
              style={{ color: branding.text_color, fontFamily: branding.font_family }}
            >
              {branding.organization_name}
            </span>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              className="px-4 py-2 rounded-lg text-white"
              style={{ backgroundColor: branding.primary_color }}
            >
              Primary Button
            </button>
            <button
              className="px-4 py-2 rounded-lg text-white"
              style={{ backgroundColor: branding.secondary_color }}
            >
              Secondary
            </button>
            <button
              className="px-4 py-2 rounded-lg text-black"
              style={{ backgroundColor: branding.accent_color }}
            >
              Accent
            </button>
          </div>
        </div>
      )}

      {/* Branding Configuration */}
      <Tabs defaultValue="identity" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="identity" className="data-[state=active]:bg-blue-600">
            <Building2 className="w-4 h-4 mr-2" />
            Identity
          </TabsTrigger>
          <TabsTrigger value="colors" className="data-[state=active]:bg-blue-600">
            <Palette className="w-4 h-4 mr-2" />
            Colors
          </TabsTrigger>
          <TabsTrigger value="typography" className="data-[state=active]:bg-blue-600">
            <Type className="w-4 h-4 mr-2" />
            Typography
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-blue-600">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="advanced" className="data-[state=active]:bg-blue-600">
            <Code className="w-4 h-4 mr-2" />
            Advanced
          </TabsTrigger>
        </TabsList>

        {/* Identity Tab */}
        <TabsContent value="identity" className="mt-6">
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-6 space-y-6">
              <div>
                <Label className="text-zinc-300">Organization Name</Label>
                <Input
                  value={branding.organization_name}
                  onChange={(e) => setBranding({ ...branding, organization_name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>

              <div>
                <Label className="text-zinc-300">Logo URL</Label>
                <Input
                  value={branding.logo_url}
                  onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-zinc-500 mt-1">Recommended: 200x50px PNG with transparent background</p>
              </div>

              <div>
                <Label className="text-zinc-300">Favicon URL</Label>
                <Input
                  value={branding.favicon_url}
                  onChange={(e) => setBranding({ ...branding, favicon_url: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  placeholder="https://example.com/favicon.ico"
                />
                <p className="text-xs text-zinc-500 mt-1">Recommended: 32x32px ICO or PNG</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Colors Tab */}
        <TabsContent value="colors" className="mt-6">
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {colorInputs.map((color) => (
                  <div key={color.key} className="space-y-2">
                    <Label className="text-zinc-300">{color.label}</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={branding[color.key]}
                        onChange={(e) => setBranding({ ...branding, [color.key]: e.target.value })}
                        className="w-12 h-12 rounded-lg cursor-pointer border-0"
                      />
                      <Input
                        value={branding[color.key]}
                        onChange={(e) => setBranding({ ...branding, [color.key]: e.target.value })}
                        className="bg-zinc-800 border-zinc-700 text-white font-mono"
                      />
                    </div>
                    <p className="text-xs text-zinc-500">{color.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Typography Tab */}
        <TabsContent value="typography" className="mt-6">
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-6 space-y-6">
              <div>
                <Label className="text-zinc-300">Font Family</Label>
                <select
                  value={branding.font_family}
                  onChange={(e) => setBranding({ ...branding, font_family: e.target.value })}
                  className="w-full mt-1 p-2 rounded-lg bg-zinc-800 border border-zinc-700 text-white"
                >
                  <option value="Space Grotesk">Space Grotesk</option>
                  <option value="Inter">Inter</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Open Sans">Open Sans</option>
                  <option value="Poppins">Poppins</option>
                  <option value="Montserrat">Montserrat</option>
                  <option value="Lato">Lato</option>
                </select>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50">
                <p className="text-zinc-400 text-sm mb-2">Preview:</p>
                <h1 className="text-3xl font-bold text-white" style={{ fontFamily: branding.font_family }}>
                  Heading Example
                </h1>
                <p className="text-zinc-300 mt-2" style={{ fontFamily: branding.font_family }}>
                  This is body text that demonstrates how your chosen font will appear throughout the application.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email" className="mt-6">
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-6 space-y-6">
              <div>
                <Label className="text-zinc-300">Email Header HTML</Label>
                <Textarea
                  value={branding.email_header_html}
                  onChange={(e) => setBranding({ ...branding, email_header_html: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 font-mono h-32"
                  placeholder="<div style='background: #3b82f6; padding: 20px;'>...</div>"
                />
                <p className="text-xs text-zinc-500 mt-1">HTML to appear at the top of all emails</p>
              </div>

              <div>
                <Label className="text-zinc-300">Email Footer HTML</Label>
                <Textarea
                  value={branding.email_footer_html}
                  onChange={(e) => setBranding({ ...branding, email_footer_html: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 font-mono h-32"
                  placeholder="<div style='text-align: center; color: #666;'>© 2026 Your Company</div>"
                />
                <p className="text-xs text-zinc-500 mt-1">HTML to appear at the bottom of all emails</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Advanced Tab */}
        <TabsContent value="advanced" className="mt-6">
          <Card className="glass-card border-zinc-800">
            <CardContent className="p-6 space-y-6">
              <div>
                <Label className="text-zinc-300">Custom CSS</Label>
                <Textarea
                  value={branding.custom_css}
                  onChange={(e) => setBranding({ ...branding, custom_css: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 font-mono h-48"
                  placeholder={`.custom-button { background: linear-gradient(...); }`}
                />
                <p className="text-xs text-zinc-500 mt-1">Custom CSS to override default styles</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveBranding}
          className="bg-blue-600 hover:bg-blue-500"
          disabled={!selectedOrg}
          data-testid="save-branding-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          Save Branding
        </Button>
      </div>

      {/* Create Organization Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk' }}>
              Create Organization
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOrganization} className="space-y-4">
            <div>
              <Label className="text-zinc-300">Organization Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="Acme Corp"
                required
              />
            </div>

            <div>
              <Label className="text-zinc-300">Custom Domain (Optional)</Label>
              <Input
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                placeholder="ads.acme.com"
              />
              <p className="text-xs text-zinc-500 mt-1">Point your domain's CNAME to our servers</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-500">
                Create Organization
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
